import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../../hooks/useAuth";
import { RootState } from "../../store";
import { Box, CircularProgress } from "@mui/material";
import useRBAC from "../../hooks/useRBAC";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: number;
  requiredRoles?: number[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredRoles,
}) => {
  const { token, loading, user } = useAppSelector((state: RootState) => state.auth);
  const { canAccessPage } = useRBAC();
  const isAuthenticated = !!token;
  const isLoading = loading;
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user can access the current page
  if (!canAccessPage(location.pathname)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check specific role requirements if provided
  const userRoleId = user?.roleid;
  const isRoleDenied =
    (requiredRole && userRoleId !== requiredRole) ||
    (requiredRoles && userRoleId && !requiredRoles.includes(userRoleId));

  if (isRoleDenied) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
